import { useState, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { APP_PATHS } from '@/config/routes/app-paths';
import { useAppStore } from '@/stores/app-store';
import { ensureValidPath } from '@/utils/validate-path';
import { isAxiosError } from '@/lib/axios';
import { fetchProfile, redeemSignInCredential } from '@/lib/auth-api';
import type { LoginErrorResponse } from '@/types/auth';
import type { RedeemCredentialPayload, SignInResult } from '@/types/auth-credential';

export function useAuth() {
  const [isLoading, setIsLoading] = useState(false);
  const setIsAuthenticated = useAppStore((s) => s.setIsAuthenticated);
  const clearSession = useAppStore((s) => s.clearSession);
  const startSession = useAppStore((s) => s.startSession);
  const setCurrentUser = useAppStore((s) => s.setCurrentUser);
  const isAuthenticated = useAppStore((s) => s.isAuthenticated);
  const navigate = useNavigate();

  /**
   * Turn an access token into a live session: store it, load the profile it unlocks, and mark the
   * app authenticated. Exported because activation now ends signed in too — redeeming an
   * activation credential returns the same full session token as signing in, so the activation
   * flow establishes its session through this rather than duplicating the sequence.
   */
  const establishSession = useCallback(
    async (accessToken: string): Promise<void> => {
      sessionStorage.setItem('access_token', accessToken);
      const user = await fetchProfile();
      setCurrentUser(user);
      setIsAuthenticated(true);
      startSession();
    },
    [setCurrentUser, setIsAuthenticated, startSession],
  );

  /**
   * Redeem a one-time sign-in credential and land on the dashboard.
   *
   * A rejected credential is a 200 with `success: false`, so it is returned as `{ ok: false }`
   * rather than thrown — the backend's message is deliberately the same for expired, reused,
   * wrong-code and unknown-token, and is meant to be shown verbatim. Transport failures still
   * throw and remain the caller's to catch.
   */
  const redeem = useCallback(
    async (payload: RedeemCredentialPayload, emailForRedirect?: string): Promise<SignInResult> => {
      setIsLoading(true);
      try {
        const response = await redeemSignInCredential(payload);

        if (!response.success || !response.accessToken) {
          return { ok: false, error: response.error };
        }

        await establishSession(response.accessToken);
        void navigate({ to: ensureValidPath(APP_PATHS.overview) });
        return { ok: true };
      } catch (err) {
        // The sign-in endpoints are deliberately silent about unactivated accounts, so this 403 now
        // only reaches us from fetchProfile — but the redirect it drives is still the right one.
        if (isAxiosError(err) && err.response?.status === 403) {
          const data = err.response.data as LoginErrorResponse;
          if (data.reason === 'needs_activation' || data.reason === 'not_activated') {
            void navigate({
              to: ensureValidPath(APP_PATHS.pendingActivation),
              search: { email: emailForRedirect ?? data.email },
            });
            return { ok: false };
          }
        }
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [establishSession, navigate],
  );

  const signIn = useCallback(
    (email: string, code: string): Promise<SignInResult> => redeem({ email, code }, email),
    [redeem],
  );

  /** The hotlink path — same redemption, spending the token from the emailed link instead. */
  const signInWithToken = useCallback(
    (token: string): Promise<SignInResult> => redeem({ token }),
    [redeem],
  );

  const signOut = useCallback((): void => {
    // The real backend has no /auth/logout endpoint — session refresh is cookie-based, so
    // signing out is purely local cleanup (clearing the access token forces re-login).
    sessionStorage.removeItem('access_token');
    clearSession();
    // Every unauthenticated redirect in the app lands on the landing page's sign-in tab
    // (require-auth-before-load, use-session-timeout, use-auth-expired) — /signin exists for the
    // emailed hotlink, not as a second canonical destination.
    void navigate({ to: ensureValidPath(APP_PATHS.home), search: { tab: 'signin' } });
  }, [navigate, clearSession]);

  return { isAuthenticated, isLoading, signIn, signInWithToken, signOut, establishSession };
}
